import React, { useRef, useState } from 'react';
import {
  doc,
  runTransaction,
  serverTimestamp,
} from 'firebase/firestore';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import swal from 'sweetalert';

import { db } from '../../services/firebase';
import Banner from '../Banners/Banner';

const crearDatosIniciales = () => ({
  fecha: obtenerFechaActual(),
  paciente: '',
  monto: '',
  formaPago: '',
  concepto: '',
});

const Recibo = () => {
  const reciboRef = useRef(null);

  const [recibo, setRecibo] = useState(crearDatosIniciales);
  const [numeroRecibo, setNumeroRecibo] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [descargando, setDescargando] = useState(false);
  const [reciboGuardado, setReciboGuardado] = useState(false);

  const actualizarCampo = (event) => {
    const { name, value } = event.target;

    setRecibo((datosAnteriores) => ({
      ...datosAnteriores,
      [name]: value,
    }));
  };

  const validarFormulario = () => {
    if (!recibo.fecha) {
      swal(
        '¡Ups!',
        'Ingresá la fecha de emisión.',
        'error'
      );

      return false;
    }

    if (!recibo.paciente.trim()) {
      swal(
        '¡Ups!',
        'Ingresá el nombre de la persona o paciente.',
        'error'
      );

      return false;
    }

    const montoNumerico = normalizarMonto(recibo.monto);

    if (!montoNumerico || montoNumerico <= 0) {
      swal(
        '¡Ups!',
        'Ingresá un monto válido.',
        'error'
      );

      return false;
    }

    if (!recibo.formaPago.trim()) {
      swal(
        '¡Ups!',
        'Ingresá la forma de pago.',
        'error'
      );

      return false;
    }

    if (!recibo.concepto.trim()) {
      swal(
        '¡Ups!',
        'Ingresá el concepto del recibo.',
        'error'
      );

      return false;
    }

    return true;
  };

  const guardarRecibo = async () => {
    if (!validarFormulario()) {
      return;
    }

    setGuardando(true);

    try {
      const contadorRef = doc(
        db,
        'config',
        'recibosCounter'
      );

      const numeroGenerado = await runTransaction(
        db,
        async (transaction) => {
          const contadorSnapshot = await transaction.get(
            contadorRef
          );

          const ultimoNumero = contadorSnapshot.exists()
            ? Number(
                contadorSnapshot.data().ultimoNumero
              ) || 0
            : 0;

          const siguienteNumero = ultimoNumero + 1;

          const numeroFormateado = String(
            siguienteNumero
          ).padStart(6, '0');

          const reciboFirestoreRef = doc(
            db,
            'recibos',
            numeroFormateado
          );

          const montoNumerico = normalizarMonto(
            recibo.monto
          );

          transaction.set(
            contadorRef,
            {
              ultimoNumero: siguienteNumero,
              updatedAt: serverTimestamp(),
            },
            {
              merge: true,
            }
          );

          transaction.set(reciboFirestoreRef, {
            numero: siguienteNumero,
            numeroFormateado,
            fecha: recibo.fecha,
            paciente: recibo.paciente.trim(),
            monto: montoNumerico,
            montoLetras: convertirMontoALetras(
              montoNumerico
            ),
            formaPago: recibo.formaPago.trim(),
            concepto: recibo.concepto.trim(),
            createdAt: serverTimestamp(),
          });

          return numeroFormateado;
        }
      );

      setNumeroRecibo(numeroGenerado);
      setReciboGuardado(true);

      swal(
        'RECIBO GUARDADO',
        `El recibo N.º ${numeroGenerado} fue guardado correctamente.`,
        'success'
      );
    } catch (error) {
      console.error(
        'Error al guardar el recibo:',
        error
      );

      swal(
        '¡Ups!',
        'No se pudo guardar el recibo. Revisá la conexión y las reglas de Firebase.',
        'error'
      );
    } finally {
      setGuardando(false);
    }
  };

  const descargarPDF = async () => {
    if (!reciboGuardado || !numeroRecibo) {
      swal(
        '¡Ups!',
        'Primero tenés que guardar el recibo.',
        'error'
      );

      return;
    }

    if (!reciboRef.current) {
      return;
    }

    setDescargando(true);

    try {
      const canvas = await html2canvas(
        reciboRef.current,
        {
          scale: 3,
          useCORS: true,
          allowTaint: false,
          backgroundColor: '#ffffff',
          logging: false,
        }
      );

      const imagen = canvas.toDataURL(
        'image/png',
        1
      );

      const pdf = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: 'a5',
      });

      const anchoPagina =
        pdf.internal.pageSize.getWidth();

      const altoPagina =
        pdf.internal.pageSize.getHeight();

      const margen = 7;
      const anchoDisponible =
        anchoPagina - margen * 2;

      const altoDisponible =
        altoPagina - margen * 2;

      const proporcionImagen =
        canvas.width / canvas.height;

      let anchoImagen = anchoDisponible;
      let altoImagen =
        anchoImagen / proporcionImagen;

      if (altoImagen > altoDisponible) {
        altoImagen = altoDisponible;
        anchoImagen =
          altoImagen * proporcionImagen;
      }

      const posicionX =
        (anchoPagina - anchoImagen) / 2;

      const posicionY =
        (altoPagina - altoImagen) / 2;

      pdf.addImage(
        imagen,
        'PNG',
        posicionX,
        posicionY,
        anchoImagen,
        altoImagen
      );

      const nombrePaciente =
        prepararNombreArchivo(
          recibo.paciente
        );

      pdf.save(
        `recibo-${numeroRecibo}-${nombrePaciente}.pdf`
      );
    } catch (error) {
      console.error(
        'Error al generar el PDF:',
        error
      );

      swal(
        '¡Ups!',
        'No se pudo generar el PDF.',
        'error'
      );
    } finally {
      setDescargando(false);
    }
  };

  const crearNuevoRecibo = () => {
    setRecibo(crearDatosIniciales());
    setNumeroRecibo('');
    setReciboGuardado(false);

    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  const montoNumerico = normalizarMonto(
    recibo.monto
  );

  return (
    <section className="recibo-page">
    <Banner />
      <div className="recibo-page__container">

        {/* FORMULARIO */}

        <div className="recibo-formulario">
          <div className="recibo-formulario__header">
            <span>Generación de comprobantes</span>

            <h1>Nuevo recibo</h1>

            <p>
              Completá los datos y revisá el
              comprobante antes de guardarlo.
            </p>
          </div>

          <div className="recibo-formulario__campos">

            <div className="recibo-campo">
              <label htmlFor="fecha">
                Fecha
              </label>

              <input
                id="fecha"
                type="date"
                name="fecha"
                value={recibo.fecha}
                onChange={actualizarCampo}
                disabled={reciboGuardado}
              />
            </div>

            <div className="recibo-campo">
              <label htmlFor="paciente">
                Recibí de
              </label>

              <input
                id="paciente"
                type="text"
                name="paciente"
                placeholder="Nombre y apellido"
                value={recibo.paciente}
                onChange={actualizarCampo}
                disabled={reciboGuardado}
              />
            </div>

            <div className="recibo-campo">
              <label htmlFor="monto">
                La suma de
              </label>

              <div className="recibo-campo__monto">
                <span>$</span>

                <input
                  id="monto"
                  type="number"
                  name="monto"
                  min="0"
                  step="0.01"
                  placeholder="0"
                  value={recibo.monto}
                  onChange={actualizarCampo}
                  disabled={reciboGuardado}
                />
              </div>
            </div>

            <div className="recibo-campo">
              <label htmlFor="formaPago">
                Forma de pago
              </label>

              <input
                id="formaPago"
                type="text"
                name="formaPago"
                placeholder="Ej.: Transferencia"
                value={recibo.formaPago}
                onChange={actualizarCampo}
                disabled={reciboGuardado}
              />
            </div>

            <div className="recibo-campo">
              <label htmlFor="concepto">
                En concepto de
              </label>

              <textarea
                id="concepto"
                name="concepto"
                rows="4"
                placeholder="Detalle del concepto"
                value={recibo.concepto}
                onChange={actualizarCampo}
                disabled={reciboGuardado}
              />
            </div>

          </div>

          <div className="recibo-formulario__acciones">
            {!reciboGuardado && (
              <button
                type="button"
                className="btn-recibo btn-recibo--principal"
                onClick={guardarRecibo}
                disabled={guardando}
              >
                {guardando
                  ? 'GUARDANDO...'
                  : 'GUARDAR RECIBO'}
              </button>
            )}

            {reciboGuardado && (
              <>
                <button
                  type="button"
                  className="btn-recibo btn-recibo--principal"
                  onClick={descargarPDF}
                  disabled={descargando}
                >
                  {descargando
                    ? 'GENERANDO PDF...'
                    : 'DESCARGAR PDF'}
                </button>

                <button
                  type="button"
                  className="btn-recibo btn-recibo--secundario"
                  onClick={crearNuevoRecibo}
                >
                  CREAR NUEVO RECIBO
                </button>
              </>
            )}
          </div>
        </div>

        {/* VISTA PREVIA */}

        <div className="recibo-vista">
          <div className="recibo-vista__titulo">
            <span>Vista previa</span>

            {reciboGuardado && (
              <strong>Recibo guardado</strong>
            )}
          </div>

          <div className="recibo-preview-wrapper">
            <div
              className="recibo-preview"
              ref={reciboRef}
            >
              <header className="recibo-preview__header">

                <div className="recibo-preview__identidad">
                  <img
                    className="recibo-preview__logo"
                    src="/img/logo-federico-luque.png"
                    alt="Dr. Federico Luque"
                    crossOrigin="anonymous"
                  />

                  <div className="recibo-preview__profesional">
                    <h2>Dr. Federico Luque</h2>
                    <p>Body Sculptor</p>
                  </div>
                </div>

                <div className="recibo-preview__comprobante">
                  <strong>RECIBO</strong>

                  <span>
                    N.º{' '}
                    {numeroRecibo || '000000'}
                  </span>
                </div>
              </header>

              <div className="recibo-preview__fecha">
                Fecha:{' '}
                <strong>
                  {formatearFecha(recibo.fecha)}
                </strong>
              </div>

              <div className="recibo-preview__contenido">
                <div className="recibo-preview__renglon">
                  <span>Recibí de:</span>

                  <strong>
                    {recibo.paciente ||
                      'Nombre y apellido'}
                  </strong>
                </div>

                <div className="recibo-preview__renglon">
                  <span>La suma de:</span>

                  <strong className="recibo-preview__monto">
                    {montoNumerico > 0
                      ? formatearDinero(
                          montoNumerico
                        )
                      : '$ 0,00'}
                  </strong>
                </div>

                <div className="recibo-preview__renglon">
                  <span>Monto en letras:</span>

                  <strong>
                    {montoNumerico > 0
                      ? convertirMontoALetras(
                          montoNumerico
                        )
                      : 'CERO PESOS'}
                  </strong>
                </div>

                <div className="recibo-preview__renglon">
                  <span>Forma de pago:</span>

                  <strong>
                    {recibo.formaPago ||
                      'Forma de pago'}
                  </strong>
                </div>

                <div className="recibo-preview__renglon recibo-preview__renglon--concepto">
                  <span>En concepto de:</span>

                  <strong>
                    {recibo.concepto ||
                      'Concepto del recibo'}
                  </strong>
                </div>
              </div>

              <footer className="recibo-preview__footer">
                <div className="recibo-preview__firma">
                  <img
                    src="/img/firma-federico-luque.png"
                    alt="Firma del Dr. Federico Luque"
                    crossOrigin="anonymous"
                  />

                  <span>Firma</span>
                </div>

                <div className="recibo-preview__aclaracion">
                    <strong>
                    Dr. Federico Luque
                  </strong>
                  <span>Aclaración</span>

                  
                </div>
              </footer>
            </div>
          </div>

          <p className="recibo-vista__nota">
            El botón para descargar el PDF se
            habilita después de guardar el recibo.
          </p>
        </div>
      </div>
    </section>
  );
};

/* =====================================================
   FUNCIONES AUXILIARES
===================================================== */

function obtenerFechaActual() {
  const fecha = new Date();

  const anio = fecha.getFullYear();

  const mes = String(
    fecha.getMonth() + 1
  ).padStart(2, '0');

  const dia = String(
    fecha.getDate()
  ).padStart(2, '0');

  return `${anio}-${mes}-${dia}`;
}

function normalizarMonto(valor) {
  if (
    valor === null ||
    valor === undefined ||
    valor === ''
  ) {
    return 0;
  }

  const numero = Number(valor);

  return Number.isFinite(numero)
    ? numero
    : 0;
}

function formatearFecha(fecha) {
  if (!fecha) {
    return '';
  }

  const [anio, mes, dia] =
    fecha.split('-');

  const fechaLocal = new Date(
    Number(anio),
    Number(mes) - 1,
    Number(dia)
  );

  return fechaLocal.toLocaleDateString(
    'es-AR',
    {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }
  );
}

function formatearDinero(monto) {
  return new Intl.NumberFormat(
    'es-AR',
    {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }
  ).format(Number(monto) || 0);
}

function prepararNombreArchivo(nombre) {
  const nombrePreparado = String(
    nombre || 'paciente'
  )
    .normalize('NFD')
    .replace(
      /[\u0300-\u036f]/g,
      ''
    )
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '');

  return nombrePreparado || 'paciente';
}

function convertirMontoALetras(monto) {
  const numero = Number(monto);

  if (
    !Number.isFinite(numero) ||
    numero <= 0
  ) {
    return 'CERO PESOS';
  }

  const parteEntera = Math.floor(numero);

  const centavos = Math.round(
    (numero - parteEntera) * 100
  );

  const textoPesos =
    parteEntera === 1
      ? 'UN PESO'
      : `${numeroALetras(
          parteEntera
        )} PESOS`;

  if (centavos === 0) {
    return textoPesos;
  }

  return `${textoPesos} CON ${String(
    centavos
  ).padStart(2, '0')}/100`;
}

function numeroALetras(numero) {
  const valor = Math.floor(
    Number(numero)
  );

  if (
    !Number.isFinite(valor) ||
    valor === 0
  ) {
    return 'CERO';
  }

  if (valor < 0) {
    return `MENOS ${numeroALetras(
      Math.abs(valor)
    )}`;
  }

  if (valor > 999999999) {
    return String(valor);
  }

  const convertirMenorMil = (
    numeroMenorMil
  ) => {
    const unidades = [
      '',
      'UNO',
      'DOS',
      'TRES',
      'CUATRO',
      'CINCO',
      'SEIS',
      'SIETE',
      'OCHO',
      'NUEVE',
    ];

    const especiales = {
      10: 'DIEZ',
      11: 'ONCE',
      12: 'DOCE',
      13: 'TRECE',
      14: 'CATORCE',
      15: 'QUINCE',
      16: 'DIECISÉIS',
      17: 'DIECISIETE',
      18: 'DIECIOCHO',
      19: 'DIECINUEVE',
      20: 'VEINTE',
      21: 'VEINTIUNO',
      22: 'VEINTIDÓS',
      23: 'VEINTITRÉS',
      24: 'VEINTICUATRO',
      25: 'VEINTICINCO',
      26: 'VEINTISÉIS',
      27: 'VEINTISIETE',
      28: 'VEINTIOCHO',
      29: 'VEINTINUEVE',
    };

    const decenas = [
      '',
      '',
      'VEINTE',
      'TREINTA',
      'CUARENTA',
      'CINCUENTA',
      'SESENTA',
      'SETENTA',
      'OCHENTA',
      'NOVENTA',
    ];

    const centenas = [
      '',
      'CIENTO',
      'DOSCIENTOS',
      'TRESCIENTOS',
      'CUATROCIENTOS',
      'QUINIENTOS',
      'SEISCIENTOS',
      'SETECIENTOS',
      'OCHOCIENTOS',
      'NOVECIENTOS',
    ];

    if (numeroMenorMil === 0) {
      return '';
    }

    if (numeroMenorMil === 100) {
      return 'CIEN';
    }

    if (numeroMenorMil < 10) {
      return unidades[
        numeroMenorMil
      ];
    }

    if (numeroMenorMil < 30) {
      return especiales[
        numeroMenorMil
      ];
    }

    if (numeroMenorMil < 100) {
      const decena = Math.floor(
        numeroMenorMil / 10
      );

      const unidad =
        numeroMenorMil % 10;

      return unidad === 0
        ? decenas[decena]
        : `${decenas[decena]} Y ${unidades[unidad]}`;
    }

    const centena = Math.floor(
      numeroMenorMil / 100
    );

    const resto =
      numeroMenorMil % 100;

    return resto === 0
      ? centenas[centena]
      : `${centenas[centena]} ${convertirMenorMil(
          resto
        )}`;
  };

  const partes = [];
  let resto = valor;

  const millones = Math.floor(
    resto / 1000000
  );

  if (millones > 0) {
    if (millones === 1) {
      partes.push('UN MILLÓN');
    } else {
      partes.push(
        `${numeroALetras(
          millones
        )} MILLONES`
      );
    }

    resto %= 1000000;
  }

  const miles = Math.floor(
    resto / 1000
  );

  if (miles > 0) {
    if (miles === 1) {
      partes.push('MIL');
    } else {
      partes.push(
        `${convertirMenorMil(
          miles
        )} MIL`
      );
    }

    resto %= 1000;
  }

  if (resto > 0) {
    partes.push(
      convertirMenorMil(resto)
    );
  }

  return partes.join(' ').trim();
}

export default Recibo;